/**
 * ModuleRow - Draggable module component for reorganization
 */

import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import type { ModuleItemType, SubmoduleItem } from 'app/types/modules';
import { stripHtml } from 'core/utils/utils';
import React from 'react';
import AssetImage from '../../../../atoms/AssetImage/AssetImage';
import Button from '../../../../atoms/Button/Button';
import Icon from '../../../../atoms/Icon/Icon';
import AddItemButton from './AddItemButton';
import CreatedByInfo from './CreatedByInfo';
import SubmoduleRow from './SubmoduleRow';
import ItemActions from './itemActions';
import { useModuleRow } from './useModuleRow';

type ModuleRowProps = {
  module: ModuleItemType;
  structureLevels: 1 | 2;
  isDragOverlay?: boolean;
  reorderMode: boolean;
  isExpanded?: boolean;
  onToggleExpand?: () => void;
  onAddSubmodule?: (parentModuleGuid: string) => void;
  activeDragType?: 'module' | 'submodule' | null;
  activeParentModuleGuid?: string | null;
  onDelete?: (item: ModuleItemType | SubmoduleItem) => void;
  onEdit?: (item: ModuleItemType | SubmoduleItem) => void;
  onToggleVisibility?: (item: ModuleItemType | SubmoduleItem) => void;
  onOpenPhoto?: (item: ModuleItemType | SubmoduleItem) => void;
};

function ModuleRow({
  module,
  structureLevels,
  isDragOverlay = false,
  isExpanded = false,
  activeDragType = null,
  activeParentModuleGuid = null,
  reorderMode,
  onToggleExpand,
  onAddSubmodule,
  onDelete,
  onEdit,
  onToggleVisibility,
  onOpenPhoto,
}: ModuleRowProps): JSX.Element {
  const {
    addSubmoduleLabel,
    rootRef,
    rootStyle,
    rootClassName,
    mainClassName,
    mainDragListeners,
    isDragHandleInteractive,
    dragHandleProps,
    setDragHandleRef,
    dragHandleLabel,
    dragHandleClassName,
    imageClassName,
    imageInteractiveProps,
    assetImageId,
    moduleName,
    chevronClassName,
    chevronLabel,
    submodulesClassName,
    submodulesId,
    submodulesInertProps,
    setDroppableRef,
    submoduleIds,
    hasSubmodules,
    shouldRenderItemActions,
    shouldRenderCreatedByInfo,
    shouldRenderChevron,
    shouldRenderSubmodules,
    shouldRenderAddSubmoduleButton,
    handleToggleExpand,
    handleAddSubmodule,
  } = useModuleRow({
    module,
    structureLevels,
    isDragOverlay,
    isExpanded,
    activeDragType,
    activeParentModuleGuid,
    reorderMode,
    onToggleExpand,
    onAddSubmodule,
    onOpenPhoto,
  });

  return (
    <div ref={rootRef} style={rootStyle} className={rootClassName}>
      <div className={mainClassName} {...mainDragListeners}>
        {isDragHandleInteractive ? (
          <Button
            ref={setDragHandleRef}
            {...dragHandleProps}
            className={dragHandleClassName}
            variant="plain"
            isIconOnly
            leftIcon="drag-bullets"
            iconSize="sm"
            ariaLabel={dragHandleLabel}
          />
        ) : (
          <div className={dragHandleClassName}>
            <Icon name="drag-bullets" size="sm" color="grey-03" />
          </div>
        )}

        <div className={imageClassName} {...imageInteractiveProps}>
          <AssetImage src={module?.image} id={assetImageId} alt={moduleName} />
        </div>

        <div className="module-row__info">
          <span className="module-row__name">{stripHtml(moduleName)}</span>
        </div>
        {shouldRenderItemActions && <ItemActions item={module} onDelete={onDelete} onEdit={onEdit} onToggleVisibility={onToggleVisibility} />}
        {shouldRenderCreatedByInfo && (
          <div className="module-row__created-by">
            <CreatedByInfo avatar={module.created_by?.avatar} name={module.created_by?.name} role={module.created_by?.role_guid} />
          </div>
        )}
        {shouldRenderChevron && (
          <Button
            className={chevronClassName}
            variant="plain"
            isIconOnly
            leftIcon="chevron-down"
            iconSize="md"
            ariaLabel={chevronLabel}
            ariaExpanded={isExpanded}
            ariaControls={submodulesId}
            onClick={handleToggleExpand}
            text=""
          />
        )}
      </div>
      {shouldRenderSubmodules && (
        <div ref={setDroppableRef} id={submodulesId} className={submodulesClassName} {...submodulesInertProps}>
          <SortableContext items={submoduleIds} strategy={verticalListSortingStrategy}>
            {hasSubmodules &&
              module.items.map((submodule) => (
                <SubmoduleRow
                  key={submodule.guid}
                  submodule={submodule}
                  reorderMode={reorderMode}
                  onDelete={onDelete}
                  onEdit={onEdit}
                  onToggleVisibility={onToggleVisibility}
                  onOpenPhoto={onOpenPhoto}
                />
              ))}
          </SortableContext>

          {shouldRenderAddSubmoduleButton && <AddItemButton label={addSubmoduleLabel} onClick={handleAddSubmodule} variant="submodule" />}
        </div>
      )}
    </div>
  );
}

export default ModuleRow;
